import React, { useEffect, useState } from 'react';
import { Google as GoogleIcon, Email as EmailIcon, Lock as LockIcon, Microsoft } from '@mui/icons-material';
import { auth, googleProvider, microsoftProvider, appCheck } from '../firebaseConfig.tsx';
import { onAuthStateChanged, signInWithPopup, signInWithEmailAndPassword, sendPasswordResetEmail, sendEmailVerification } from 'firebase/auth';

import { getToken } from 'firebase/app-check';
import './AuthPage.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';

import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useLocation } from 'react-router-dom';
import LocalizedLink from './LocalizedLink';
import { SEO } from './SEO';
import {
  buildAdIntentSearchParams,
  clearPendingAdMode,
  getAdModeFromSearchParams,
  getPostAuthAdDestination,
  readPendingAdMode,
  storePendingAdMode,
} from '../utils/adLandingIntent';
import {
  cacheAuthUser,
  clearCachedAuthUser,
  ensureAuthUserDoc,
  ensureUserErrorMessage,
  isEnsureUserBlockedError,
  providerIdForUser,
} from '../utils/authUserDoc';

const RESEND_COOLDOWN_SECONDS = 60;
function formatResendWait(seconds: number) {
  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return remainder > 0 ? `${minutes}m ${remainder}s` : `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  return `${seconds} second${seconds === 1 ? '' : 's'}`;
}

/** Redirect to a pending enterprise invite if one exists, otherwise to /chat. */
function redirectAfterLogin(navigate: ReturnType<typeof import('../hooks/useLocalizedNavigate').useLocalizedNavigate>) {
  const pending = sessionStorage.getItem('pendingEnterpriseInvite');
  if (pending) {
    navigate(`/invite/${pending}`);
    return;
  }

  const pendingAdMode = readPendingAdMode();
  if (pendingAdMode) {
    clearPendingAdMode();
    navigate(getPostAuthAdDestination(pendingAdMode));
    return;
  }

  navigate('/chat');
}

function AuthPage() {
  const navigate = useLocalizedNavigate();
  const { t } = useTranslation('auth');
  const location = useLocation();
  const locationSearchParams = new URLSearchParams(location.search);
  const requestedMode = getAdModeFromSearchParams(locationSearchParams);
  const forwardedAuthSearch = buildAdIntentSearchParams(locationSearchParams).toString();
  const signUpHref = forwardedAuthSearch ? `/sign-up?${forwardedAuthSearch}` : '/sign-up';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isVerificationPrompt, setIsVerificationPrompt] = useState(false);
  const [unverifiedUser, setUnverifiedUser] = useState<any>(null);
  const [showVerifiedBanner, setShowVerifiedBanner] = useState(false);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [resendLimitReached, setResendLimitReached] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        clearCachedAuthUser();
        return;
      }
      if (!user.emailVerified) return;
      try {
        await ensureAuthUserDoc(user, {
          provider: providerIdForUser(user),
          source: 'login_page_existing_session',
          includeDeviceInfo: false,
        });
        cacheAuthUser(user);
        if (requestedMode) {
          clearPendingAdMode();
          navigate(getPostAuthAdDestination(requestedMode));
        } else {
          redirectAfterLogin(navigate);
        }
      } catch (error) {
        console.warn('Unable to repair existing login session before redirect.', error);
        clearCachedAuthUser();
        if (isEnsureUserBlockedError(error)) {
          try {
            await auth.signOut();
          } catch {}
          toast.error(ensureUserErrorMessage(error));
        }
      }
    });
    return () => unsubscribe();
  }, [navigate, requestedMode, t]);

  // Show success toast when redirected after email verification + pre-fill email
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('verified') === '1') {
      toast.success(
        t('auth.email_verified_success', { defaultValue: 'Email verified successfully! Please log in.' }),
        { autoClose: 5000 }
      );
      setShowVerifiedBanner(true);
      // Pre-fill email from verification flow (stored by SignPage via checkActionCode)
      const verifiedEmail = sessionStorage.getItem('verifiedEmail');
      if (verifiedEmail) {
        setEmail(verifiedEmail);
        sessionStorage.removeItem('verifiedEmail');
      }
    } else {
      setShowVerifiedBanner(false);
    }
  }, [location.search, t]);

  useEffect(() => {
    if (resendCooldownSeconds <= 0) return undefined;
    const intervalId = window.setInterval(() => {
      setResendCooldownSeconds((current) => (current > 1 ? current - 1 : 0));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [resendCooldownSeconds]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const [loading, setLoading] = useState(false);

  const handleEmailLogin = async (e: React.FormEvent) => {
    setLoading(true);
    e.preventDefault();
    if (requestedMode) storePendingAdMode(requestedMode);
    
    if (!email || !password) {
      toast.error(t('auth.fill_all_fields'));
      setLoading(false);
      return;
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        setUnverifiedUser(user);
        setIsVerificationPrompt(true);
        setLoading(false);
        await auth.signOut();
        return;
      }else{

      await ensureAuthUserDoc(user, {
        provider: providerIdForUser(user, 'password'),
        source: 'email_login',
        includeDeviceInfo: false,
      });
      cacheAuthUser(user);
      toast.success(t('auth.login_success'));
      redirectAfterLogin(navigate);
      }
    } catch (error: any) {
      clearCachedAuthUser();
      setLoading(false);
      if (isEnsureUserBlockedError(error)) {
        try {
          await auth.signOut();
        } catch {}
        toast.error(ensureUserErrorMessage(error));
        return;
      }
      const errMsg = error?.message || error?.code || '';
      if (errMsg.includes('app-check') || errMsg.includes('appCheck') || errMsg.includes('app_check')) {
        if (appCheck) {
          try {
            await getToken(appCheck, true);
            toast.warning(t('auth.session_refreshed_login'));
          } catch {
            toast.error(t('auth.session_expired'));
          }
        } else {
          toast.error(t('auth.session_expired'));
        }
      } else {
        toast.error(t('auth.error_logging_in_verify_credentials'));
      }
    }
    setLoading(false);
  };

  const handleResendVerification = async () => {
    if (resendCooldownSeconds > 0) {
      toast.info(
        t('auth.resend_wait_time', {
          defaultValue: "You can resend the email in {{time}} if it hasn't arrived.",
          time: formatResendWait(resendCooldownSeconds),
        })
      );
      return;
    }

    try {
      if (unverifiedUser) {
        await sendEmailVerification(unverifiedUser);
        setResendLimitReached(false);
        toast.success(t('auth.verification_email_resent'));
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        await sendEmailVerification(userCredential.user);
        await auth.signOut();
        setResendLimitReached(false);
        toast.success(t('auth.verification_email_resent'));
      }
      setResendCooldownSeconds(RESEND_COOLDOWN_SECONDS);
    } catch (error: any) {
      if (error?.code === 'auth/too-many-requests') {
        setResendCooldownSeconds(0);
        setResendLimitReached(true);
        toast.warning(
          t('auth.resend_limit_reached_toast', {
            defaultValue: 'Too many verification emails were requested recently. Please check your inbox or spam folder, or use a different email address.',
          })
        );
        return;
      }
      toast.error(t('auth.error_resending_verification'));
    }
  };

  const handleSocialLogin = async (provider: any, providerName: string) => {
    try {
      if (requestedMode) storePendingAdMode(requestedMode);
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      await ensureAuthUserDoc(user, {
        provider: providerIdForUser(user, providerName.toLowerCase()),
        source: `${providerName.toLowerCase()}_login`,
      });
      cacheAuthUser(user);
      toast.success(t('auth.logged_in_with_provider', { providerName }));
      redirectAfterLogin(navigate);
    } catch (error: any) {
      console.error(error);
      clearCachedAuthUser();
      try {
        await auth.signOut();
      } catch {}
      if (isEnsureUserBlockedError(error)) {
        toast.error(ensureUserErrorMessage(error));
        return;
      }
      const errMsg = error?.message || error?.code || '';
      if (errMsg.includes('app-check') || errMsg.includes('appCheck') || errMsg.includes('app_check')) {
        // Auto-retry: force-refresh the App Check token
        if (appCheck) {
          try {
            await getToken(appCheck, true);
            toast.warning(t('auth.session_refreshed_login_provider', { providerName }));
          } catch {
            toast.error(t('auth.session_expired'));
          }
        } else {
          toast.error(t('auth.session_expired'));
        }
      } else {
        toast.error(t('auth.error_logging_in_with_provider', { providerName }));
      }
    }
  };

  const handleGoogleLogin = () => handleSocialLogin(googleProvider, 'Google');
  const handleMicroSoftLogin = () => handleSocialLogin(microsoftProvider, 'Microsoft');

  const handleForgotPassword = async () => {
    setLoading(true);
    if (requestedMode) {
      storePendingAdMode(requestedMode);
    }
    if (!email) {
      toast.error(t('auth.email_required'));
      setLoading(false);
      return;
    }

    try {
      // Directly request password reset via Firebase Auth.
      // Avoid Firestore queries here to prevent permission errors and user enumeration.
      await sendPasswordResetEmail(auth, email);
      toast.success(t('auth.password_reset_email_sent'));
      setLoading(false);
      
    } catch (error:any) {
      // Treat "user-not-found" as success to avoid disclosing account existence
      if (error?.code === 'auth/user-not-found') {
        toast.success(t('auth.password_reset_email_sent'));
      } else {
        toast.error(t('auth.error_sending_password_reset_email') + ': ' + error.message);
      }
      setLoading(false);
    }
};

  const backToLogin = () => {
    setIsVerificationPrompt(false);
    setUnverifiedUser(null);
    setResendCooldownSeconds(0);
    setResendLimitReached(false);
  };

  if (isVerificationPrompt) {
    return (
      <div className="auth-container">
        <SEO title={t('auth.page_title')} />
        <div className="overlay"></div>
        <div className="content">
          <div className="w-full max-w-md space-y-6 rounded-md text-[#111111]">
            <div className="text-center">
              <h2 className="text-3xl font-extrabold">{t('auth.title')}</h2>
              <br/>
              <p className="text-sm text-gray-600">{t('auth.tagline1')}</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-center mb-4">{t('auth.email_not_verified')}</h3>
              <p className="text-center mb-6">{t('auth.email_not_verified_msg', { email })}</p>
              
              <div className="text-center mb-4">
                {resendLimitReached ? (
                  <div className="space-y-2 text-center">
                    <p className="text-sm font-semibold text-amber-700">
                      {t('auth.resend_limit_reached_title', {
                        defaultValue: 'Verification email limit reached',
                      })}
                    </p>
                    <p className="text-sm text-gray-500">
                      {t('auth.resend_limit_reached_body', {
                        defaultValue: "You've requested too many verification emails for this address. Please check your inbox and spam folder. If you still can't find it, please wait at least an hour before trying again, or sign up with a different email address.",
                      })}
                    </p>
                  </div>
                ) : resendCooldownSeconds > 0 ? (
                  <p className="text-sm text-gray-500">
                    {t('auth.resend_available_in', {
                      defaultValue: "You can resend the confirmation email in {{time}} if it hasn't arrived yet.",
                      time: formatResendWait(resendCooldownSeconds),
                    })}
                  </p>
                ) : (
                  <>
                    <button
                      onClick={handleResendVerification}
                      className="text-blue-600 hover:underline focus:outline-none"
                    >
                      {t('auth.resend_email')}
                    </button>
                    <p className="mt-2 text-xs text-gray-500">
                      {t('auth.resend_email_hint', {
                        defaultValue: '(Please check your spam or bulk mail folder as well.)',
                      })}
                    </p>
                  </>
                )}
              </div>
              
              <button
                onClick={backToLogin}
                className="w-full py-2 px-4 bg-black text-white rounded-md hover:bg-gray-800 transition-colors mt-4"
              >
                {t('auth.back_to_login')}
              </button>
            </div>
          </div>
        </div>
        <ToastContainer position='bottom-right' />
      </div>
    );
  }

  return (
    <div style={{position:"relative"}} className="auth-container">
      <SEO title={t('auth.page_title')} />
      <div className="overlay"></div>
      <img className="hidden xl:block" style={{position:"absolute",left:"20%",width:"150px",top:"42%", transform: "translate(-50%, -50%)"}} src="/lightning.png"></img>
      <div className="content">
       
        <div className="w-full max-w-md space-y-6 rounded-md text-[#111111]">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold">{t('auth.title')}</h2>
            <br/>
            <p className="text-sm text-gray-700 font-medium">{t('auth.tagline1')}</p>
            <p className="text-sm text-gray-500 mt-2">{t('auth.tagline2')}</p>
          </div>
          {showVerifiedBanner && (
            <p className="text-sm bg-green-50 rounded-lg text-green-500 my-2 p-2 text-center">
              {`${t('auth.verification_success_title', { defaultValue: 'Verification successful' })}! ${t('auth.verification_success_message', { defaultValue: 'Please log in to continue.' })}`}
            </p>
          )}
          <form className="mt-4 space-y-6" onSubmit={handleEmailLogin}>
            <div className="flex items-center border border-gray-300 rounded-md px-3 py-2">
              <EmailIcon className="text-gray-500 mr-2" />
              <input
                type="email"
                placeholder={t('auth.email')}
                className="w-full focus:outline-none"
                name="email"
                value={email}
                onChange={handleEmailChange}
              />
            </div>
            <div className="flex items-center border border-gray-300 rounded-md px-3 py-2">
              <LockIcon className="text-gray-500 mr-2" />
              <input
                type="password"
                placeholder={t('auth.password')}
                className="w-full focus:outline-none"
                name="password"
                value={password}
                onChange={handlePasswordChange}
              />
            </div>
            <button
              type="submit"
              className="w-full py-2 px-4 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
            >
              {loading ? <span className="spinner" /> : t('auth.login_now')}
            </button>
            <div className="flex justify-between space-x-4 text-sm text-gray-600 mt-4">
            <LocalizedLink to={signUpHref} className="underline hover:text-gray-700">{t('auth.sign_up_link')}</LocalizedLink>
           
              <button 
                type="button" 
                disabled={loading}
                onClick={handleForgotPassword} 
                className="underline hover:text-gray-700"
              >
                {t('auth.forgot_password')}
              </button>
             
            </div>
            
            <div className="flex justify-center space-x-4 text-sm text-gray-600 mt-4"><p>{t('auth.or_continue_with')}</p></div>
            <div style={{flexDirection:"column"}} className="flex justify-center space-x-4 mt-2">
              <button 
                type="button" 
                onClick={handleGoogleLogin} 
                className="flex justify-center items-center px-4 py-2 border border-gray-300 rounded-full btn-hover-auth m-2"
              >
                <GoogleIcon className="w-5 h-5 mr-2" />
                {t('auth.login_with_google')}
              </button>
              {/* <button 
                type="button" 
                onClick={handleFacebookLogin} 
                className="flex justify-center items-center px-4 py-2 border border-gray-300 rounded-full hover:bg-gray-100 transition-colors m-2"
              >
                <FacebookIcon className="w-5 h-5 mr-2" />
                Login with Facebook
              </button> */}
              <button 
                type="button" 
                onClick={handleMicroSoftLogin} 
                className="flex justify-center items-center px-4 py-2 border border-gray-300 rounded-full btn-hover-auth m-2"
              >
                <Microsoft className="w-5 h-5 mr-2" />
                {t('auth.login_with_microsoft')}
              </button>
            </div>
          </form>
          <div className="text-center text-sm text-gray-600">
            <p>
              {t('auth.agree_text')}{' '}
              <LocalizedLink to="/terms-condition" className="underline hover:text-gray-700">{t('auth.terms')}</LocalizedLink> and{' '}
              <LocalizedLink to="/privacy-policy" className="underline hover:text-gray-700">{t('auth.privacy')}</LocalizedLink>.
            </p>
          </div>
        </div>
        <ToastContainer position='bottom-right' />
      </div>
    </div>
  );
}

export default AuthPage;
